import React from 'react';
import moment from 'moment';
import makeStyles from '@mui/styles/makeStyles';
import { Done, DoneAll, ErrorOutline } from '@mui/icons-material';
import { grey, green, red } from '@mui/material/colors';
import { Box, Stack, Typography } from '@mui/material';
import { SmsDirection, SmsActivityStatus } from '@trnsact/trnsact-shared-types';
import { DealSmsActivity } from '../../api/types';
import { DateFormats } from '../../../../constants/common';
import { generatePersonaFullName } from 'utils/formatters';
import { useSelector } from 'react-redux';

interface Props {
  message: DealSmsActivity;
}

export const MessageBubble = ({ message }: Props) => {
  const currentUserProfile = useSelector((state: any) => state.userProfile);

  const isOutgoing = message.smsDirection === SmsDirection.Outgoing;
  const isUnread = message.smsDirection === SmsDirection.Incoming && message.status !== SmsActivityStatus.Read;
  const isFailed = message.status === SmsActivityStatus.Failed;

  const { ownerPgCreditApplication } = message;
  const contactName = generatePersonaFullName(ownerPgCreditApplication);

  const senderName = generatePersonaFullName(message.createdByUserProfile);

  const classes = useStyles({ isUnread, isOutgoing });

  return (
    <Box className={classes.messageBubble}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap="1rem">
        <Stack flex={1} gap="0.25rem">
          <Typography variant="caption" fontWeight={500}>
            {isOutgoing ? (currentUserProfile.id === message.createdByUserProfileId ? 'You' : senderName) : contactName}
          </Typography>

          <Typography variant="body2" fontSize={16} sx={{ whiteSpace: 'pre-line' }}>
            {message.message}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" gap="0.25rem" alignSelf="flex-end">
          <Typography variant="caption" color={isOutgoing ? 'white' : 'textSecondary'}>
            {moment(message.createdDateTime).format(DateFormats.DateTimeMedium12Hour)}
          </Typography>

          {isOutgoing && message.status === SmsActivityStatus.Sent && <Done className={classes.sentIcon} />}
          {isOutgoing && message.status === SmsActivityStatus.Delivered && <DoneAll className={classes.sentIcon} />}
          {isFailed && <ErrorOutline sx={{ fontSize: 14, color: red[400] }} />}
        </Stack>
      </Stack>
    </Box>
  );
};

const useStyles = makeStyles({
  messageBubble: (props: { isUnread: boolean; isOutgoing: boolean }) => ({
    padding: '0.5rem 0.75rem',
    borderRadius: props.isOutgoing ? '20px 20px 1px 20px' : '20px 20px 20px 1px',
    backgroundColor: props.isOutgoing ? green[500] : grey[200],
    color: props.isOutgoing ? 'white' : 'inherit',
    alignSelf: props.isOutgoing ? 'flex-end' : 'flex-start',
  }),
  sentIcon: {
    fontSize: 16,
    color: 'white',
  },
});
